import { inject, provide, ref, readonly } from 'vue';
import type { InjectionKey, Ref } from 'vue';

interface CodeBlock {
  currentCodeExampleId: Readonly<Ref<string>>;
  setCurrentCodeId: (codeId: string) => void;
}

const provideSymbol: InjectionKey<CodeBlock> = Symbol();

const provideCodeBlocks = (data: CodeBlock) => {
  provide(provideSymbol, data);
};

/**
 * Composable that provides and manages the currently active code example in the docs layout.
 * Tracks the active code block id, resolves the corresponding DOM element, and provides
 * the state to descendants via Vue's provide/inject. Call once in a parent (e.g. Layout)
 * that wraps both the sidebar/toc and the code rails.
 *
 * @returns Object with:
 *   - currentCodeExampleId – readonly ref to the id of the active code example.
 *   - setCurrentCodeId – function to set the active code example by id (updates highlight).
 *
 * @example
 * // In a layout/parent component (e.g. Layout.vue)
 * setup() {
 *   const { currentCodeExampleId, setCurrentCodeId } = useCodeBlocks();
 *   return { currentCodeExampleId, setCurrentCodeId };
 * }
 *
 * @example
 * // In a child that renders code links (e.g. sidebar or code rails)
 * const { setCurrentCodeId } = useCurrentCodeBlock();
 * function onCodeLinkClick(codeId: string) {
 *   setCurrentCodeId?.(codeId);
 * }
 */
export const useCodeBlocks = () => {
  const currentCodeExampleId = ref<string>();

  const setCurrentCodeId = (codeId: string) => {
    currentCodeExampleId.value = codeId;
  };

  const result = {
    currentCodeExampleId: readonly(currentCodeExampleId),
    setCurrentCodeId,
  };

  provideCodeBlocks(result);

  return result;
};

/**
 * Injects the code-blocks context provided by an ancestor that called useCodeBlocks.
 * Use in descendants (e.g. sidebar, code rails) to read or update the active code example.
 *
 * @returns The CodeBlock context (currentCodeLinkElement, currentCodeExampleId, setCurrentCodeId)
 *   or undefined if no provider is in the tree.
 *
 * @example
 * const codeBlocks = useCurrentCodeBlock();
 * if (codeBlocks) {
 *   codeBlocks.setCurrentCodeId('example-getClient');
 * }
 */
export const useCurrentCodeBlock = () => {
  const result = inject(provideSymbol);
  if (!result) {
    throw new Error('useCurrentCodeBlock is not provided');
  }
  return result;
};
